import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  UrlLookupResult,
  addExcludedItem,
  addPinnedItem,
  fetchAdminState,
  fetchRecommendations,
  generateRecommendations,
  lookupContentByUrl,
  removeExcludedItem,
  removePinnedItem,
  reorderPinnedItems,
  searchContent,
  updateMutualExclude,
} from '../actions/recommendationsActions';
import { RecommendationsState, sourceKey } from '../types/recommendations.types';

const initialState: RecommendationsState = {
  bySource: {},
  adminBySource: {},
  loadingBySource: {},
  adminLoadingBySource: {},
  errorBySource: {},
  searchResults: [],
  searchLoading: false,
  urlLookupResult: null,
  urlLookupLoading: false,
};

const recommendationsSlice = createSlice({
  name: 'recommendations',
  initialState,
  reducers: {
    clearSearch(state) {
      state.searchResults = [];
    },
    clearUrlLookup(state) {
      state.urlLookupResult = null;
    },
    reorderPinnedOptimistic(state, action: PayloadAction<{ sourceType: string; sourceId: number; ids: number[] }>) {
      const { sourceType, sourceId, ids } = action.payload;
      const key = sourceKey(sourceType, sourceId);
      const adminState = state.adminBySource[key];
      if (!adminState) return;
      const byId: Record<number, typeof adminState.pinned[0]> = {};
      for (const p of adminState.pinned) byId[p.recommendation_id!] = p;
      adminState.pinned = ids
        .filter(id => byId[id])
        .map((id, i) => ({ ...byId[id], slot_position: i + 1 }));
    },
  },
  extraReducers: builder => {
    // ── Public fetch ──────────────────────────────────────────────────────
    builder.addCase(fetchRecommendations.pending, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.loadingBySource[key] = true;
      state.errorBySource[key] = null;
    });
    builder.addCase(fetchRecommendations.fulfilled, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.loadingBySource[key] = false;
      state.bySource[key] = action.payload;
    });
    builder.addCase(fetchRecommendations.rejected, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.loadingBySource[key] = false;
      state.errorBySource[key] = action.error.message ?? 'Failed to load recommendations';
    });

    // ── Admin fetch ───────────────────────────────────────────────────────
    builder.addCase(fetchAdminState.pending, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = true;
    });
    builder.addCase(fetchAdminState.fulfilled, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = false;
      state.adminBySource[key] = action.payload;
    });
    builder.addCase(fetchAdminState.rejected, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = false;
    });

    // ── Generate (run algo + refresh public cache) ────────────────────────
    builder.addCase(generateRecommendations.pending, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = true;
    });
    builder.addCase(generateRecommendations.fulfilled, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = false;
      state.adminBySource[key] = action.payload;
    });
    builder.addCase(generateRecommendations.rejected, (state, action) => {
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = false;
    });

    // ── Mutations — all reload admin state after success ──────────────────
    const reloadAdminOnSuccess = (state: RecommendationsState, action: any) => {
      // Optimistic: mark loading so the panel shows a refresh
      const key = sourceKey(action.meta.arg.sourceType, action.meta.arg.sourceId);
      state.adminLoadingBySource[key] = true;
    };

    builder.addCase(addPinnedItem.fulfilled, reloadAdminOnSuccess);
    builder.addCase(removePinnedItem.fulfilled, reloadAdminOnSuccess);
    builder.addCase(addExcludedItem.fulfilled, reloadAdminOnSuccess);
    builder.addCase(removeExcludedItem.fulfilled, reloadAdminOnSuccess);
    builder.addCase(updateMutualExclude.fulfilled, reloadAdminOnSuccess);

    // ── Search ────────────────────────────────────────────────────────────
    builder.addCase(searchContent.pending, state => {
      state.searchLoading = true;
    });
    builder.addCase(searchContent.fulfilled, (state, action) => {
      state.searchLoading = false;
      state.searchResults = action.payload;
    });
    builder.addCase(searchContent.rejected, state => {
      state.searchLoading = false;
    });

    // ── URL lookup ────────────────────────────────────────────────────────
    builder.addCase(lookupContentByUrl.pending, state => {
      state.urlLookupLoading = true;
      state.urlLookupResult = null;
    });
    builder.addCase(lookupContentByUrl.fulfilled, (state, action) => {
      state.urlLookupLoading = false;
      state.urlLookupResult = action.payload;
    });
    builder.addCase(lookupContentByUrl.rejected, state => {
      state.urlLookupLoading = false;
    });
  },
});

export const { clearSearch, clearUrlLookup, reorderPinnedOptimistic } = recommendationsSlice.actions;
export default recommendationsSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────
export const selectRecommendations = (sourceType: string, sourceId: number) =>
  (state: AppState) => state.recommendations.bySource[sourceKey(sourceType, sourceId)] ?? [];

export const selectRecommendationsLoading = (sourceType: string, sourceId: number) =>
  (state: AppState) => state.recommendations.loadingBySource[sourceKey(sourceType, sourceId)] ?? false;

export const selectAdminState = (sourceType: string, sourceId: number) =>
  (state: AppState) => state.recommendations.adminBySource[sourceKey(sourceType, sourceId)] ?? null;

export const selectAdminLoading = (sourceType: string, sourceId: number) =>
  (state: AppState) => state.recommendations.adminLoadingBySource[sourceKey(sourceType, sourceId)] ?? false;

export const selectSearchResults = (state: AppState) => state.recommendations.searchResults;
export const selectSearchLoading = (state: AppState) => state.recommendations.searchLoading;
export const selectUrlLookupResult = (state: AppState) => state.recommendations.urlLookupResult;
export const selectUrlLookupLoading = (state: AppState) => state.recommendations.urlLookupLoading;
